import {announce} from '@github-ui/aria-live'
import {selectAll} from '@github-ui/select-all'
import {useDebounce} from '@github-ui/use-debounce'
import {ValidationErrorPopover} from '@github-ui/validation-error-popover'
import {CalendarIcon, CheckIcon, XIcon} from '@primer/octicons-react'
import {FormControl, TextInput, type TextInputProps, useFormControlForwardedProps, useMergedRefs} from '@primer/react'
import {clsx} from 'clsx'
import {isAfter, isBefore, isWeekend} from 'date-fns'
import {useCallback, useEffect, useId, useImperativeHandle, useMemo, useRef, useState} from 'react'

import {useOnAnchorAction} from '../hooks/use-on-anchor-action'
import {useShortcutCalculator} from '../hooks/use-shortcut-calculator'
import {DateResources} from '../strings.en-us'
import {type AnchorElement, isRangeSelection, isSingleSelection} from '../types'
import {formatDate, getDateFormat, parseDate} from '../utils/parser'
import {HiddenDescription} from './HiddenDescription'
import styles from './Input.module.css'
import {useDatePickerContext} from './Provider'

interface UseFreeformDateInputProps {
  value: Date | null
  onChange: (value: Date | null) => void
  calendarButton?: React.ReactElement<React.HTMLProps<HTMLButtonElement>>
}

interface ValidityProps {
  status?: 'error' | 'success'
  message?: string
}

const ValidityIndicator = ({status}: Pick<ValidityProps, 'status'>) => {
  if (status === 'success') {
    return <CheckIcon className={styles.successIcon} />
  } else if (status === 'error') {
    return <XIcon className={styles.dangerIcon} />
  } else {
    return null
  }
}

/**
 * Generate props for an input where the user can type anything, but `onChange` will only
 * be called when the user has typed a valid date.
 */
const useFreeformDateInput = (
  {value, onChange, calendarButton}: UseFreeformDateInputProps,
  forwardedRef: React.Ref<HTMLInputElement>,
): {
  inputProps: Partial<TextInputProps & React.RefAttributes<HTMLInputElement>>
  format: (date: Date) => string
  resetText: (date?: Date | null) => void
  /** Run any pending validations without waiting for debounce. Returns the result of the validation and also triggers
      `onChange`. */
  flushValidate: () => Date | null | undefined
  inputRef: React.RefObject<HTMLInputElement | null>
  mergedRef: React.Ref<HTMLInputElement | null>
  validity: ValidityProps
  isFocused: () => boolean
} => {
  const inputRef = useRef<HTMLInputElement>(null)
  const mergedRef = useMergedRefs(forwardedRef, inputRef)

  const {
    configuration: {dateFormat, minDate, maxDate, disableWeekends},
  } = useDatePickerContext()

  /** Either the user's custom format function or the default formatter. */
  const format = useCallback(
    (date: Date | null) => (date ? formatDate(date, getDateFormat(dateFormat)) : ''),
    [dateFormat],
  )

  const [validity, setValidity] = useState<ValidityProps>({})
  const clearValidity = () => setValidity({})

  /** User-inputted raw (unparsed) input value. */
  const [text, setText] = useState(() => format(value))

  /**
   * Tracks the latest `value` so that deferred resets (e.g. the `setTimeout` on blur) use the
   * up-to-date value after `onChange` has propagated, rather than a stale value captured in a
   * closure. Without this, a blur reset can race with the value-sync effect and clear valid input.
   */
  const valueRef = useRef(value)
  useEffect(() => {
    valueRef.current = value
  }, [value])

  const resetText = (date: Date | null = valueRef.current) => {
    setText(format(date))
    clearValidity()
  }

  // activeElement will be null if nothing is focused
  const isFocused = useCallback(
    () => inputRef.current !== null && document.activeElement === inputRef.current,
    [inputRef],
  )

  const parse = useCallback((s: string) => parseDate(s, [dateFormat]), [dateFormat])

  /**
   * We need to parse as the user types to show their results in realtime. But we don't want to
   * overwrite their input as they type (ie, change 2/22/22 => Feb 2, 2022), so
   * we only update if not focused or if the incoming value is different from what's typed.
   */
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setText(current => (!isFocused() || parse(current)?.valueOf() !== value?.valueOf() ? format(value) : current))
  }, [value, format, isFocused, parse])

  const debouncedParseAndValidate = useDebounce(() => {
    if (!text) {
      setValidity({})
      onChange(null)
      return null
    }

    const parsed = parse(text)

    if (!parsed) {
      setValidity({status: 'error'})
      onChange(null)
    } else if (minDate && isBefore(parsed, minDate)) {
      const message = DateResources.beforeMinDate(minDate)
      setValidity({status: 'error', message})
      announce(message, {assertive: true})
      onChange(null)
    } else if (maxDate && isAfter(parsed, maxDate)) {
      const message = DateResources.afterMaxDate(maxDate)
      setValidity({status: 'error', message})
      announce(message, {assertive: true})
      onChange(null)
    } else if (disableWeekends && isWeekend(parsed)) {
      setValidity({status: 'error', message: DateResources.weekend})
      announce(DateResources.weekend, {assertive: true})
      onChange(null)
    } else {
      setValidity({status: 'success'})
      onChange(parsed)
    }

    return parsed
  }, 500)

  const changeHandler = ({target}: React.ChangeEvent<HTMLInputElement>) => {
    setText(target.value)
    debouncedParseAndValidate()
  }

  const onFocus = useCallback(() => {
    if (text) {
      setValidity({status: 'success'})
    } else {
      setValidity({})
    }
  }, [text, setValidity])

  const flushValidate = () => debouncedParseAndValidate.flush()

  const ValidIcon: React.ComponentType = () => <ValidityIndicator {...validity} />

  const inputButtonProps = calendarButton
    ? {leadingVisual: ValidIcon, trailingAction: calendarButton}
    : {trailingVisual: ValidIcon}

  return {
    inputProps: {
      value: text,
      onChange: changeHandler,
      onFocus,
      onBlur: () => clearValidity(),
      ref: mergedRef,
      validationStatus: validity.status === 'error' ? 'error' : undefined,
      'aria-invalid': validity.status === 'error',
      ...inputButtonProps,
    },
    format,
    flushValidate,
    resetText,
    inputRef,
    mergedRef,
    validity,
    isFocused,
  }
}

type SingleDateInputProps = {
  value: Date | null
  onChange: (value: Date) => void
  onFocus?: () => void
  placeholder?: string
  'aria-label'?: string
  fullWidth?: boolean
  disableShortcuts?: boolean
  activeRangeEnd?: boolean
  calendarButton?: React.ReactElement<React.HTMLProps<HTMLButtonElement>>
  disabled?: boolean
  className?: string
  labelVisible?: boolean
  isAnchor: boolean
}

const SingleDateInput = ({
  ref: forwardedRef,
  value,
  onChange,
  placeholder,
  'aria-label': ariaLabel,
  onFocus,
  fullWidth,
  disableShortcuts = false,
  calendarButton,
  activeRangeEnd = false,
  className,
  disabled,
  labelVisible = false,
  isAnchor,
}: SingleDateInputProps & {ref: React.Ref<HTMLInputElement>}) => {
  const {inputProps, resetText, flushValidate, inputRef, mergedRef, validity} = useFreeformDateInput(
    {value, onChange: d => d && onChange(d), calendarButton},
    forwardedRef,
  )
  const withFormControlProps = useFormControlForwardedProps(inputProps)

  /**
   * In arrow key mode, all text is always selected and the arrow keys can be used to
   * increment/decrement the value. Typing or clicking again exits arrow key mode.
   */
  const arrowKeyModeRef = useRef(false)
  // Needs to be in a timeout to select all after the value of the input is set, or the selection will clear when value changes
  // eslint-disable-next-line react-hooks/refs
  setTimeout(() => {
    // Querying arrowKeyModeRef.current here inside the timeout allows cancelling the select all
    // on blur to avoid refocusing the input(in Safari). This is why we use ref instead of state.
    if (arrowKeyModeRef.current) selectAll(inputRef)
  })

  const calculateShortcut = useShortcutCalculator()
  const keyDownHandler = (e: React.KeyboardEvent) => {
    if (e.defaultPrevented || !arrowKeyModeRef.current || !value) return

    const newValue = calculateShortcut(value, e)
    if (newValue) {
      e.preventDefault()
      onChange(newValue)
      resetText(newValue)
    }
  }

  const descriptionId = useId()
  const errorId = useId()

  const input = (
    <TextInput
      {...(isAnchor ? withFormControlProps : inputProps)}
      onFocus={e => {
        inputProps.onFocus?.(e)
        if (!disableShortcuts) arrowKeyModeRef.current = true
        onFocus?.()
      }}
      onBlur={e => {
        arrowKeyModeRef.current = false
        inputProps.onBlur?.(e)

        flushValidate()
        // We can't just reset to the value returned by `flushValidate` because we don't know if it's acceptable;
        // it might for example fall outside the min/max date range, which we can't know until the full
        // onChange > state update > rerender cycle completes
        setTimeout(() => resetText())
      }}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        inputProps.onChange?.(e)
        arrowKeyModeRef.current = false
      }}
      onKeyDown={keyDownHandler}
      // Order is onMouseDown -> onFocus -> onClick -> onMouseUp, so mouseDown will set false and then
      // focus will set true, unless it was already focused in which case it will stay false.
      // This lets the user click again inside the input to exit arrow key mode.
      onMouseDown={() => (arrowKeyModeRef.current = false)}
      onMouseUp={() => {
        // Sometimes if the user moves their mouse slightly on the initial click, the browser will treat it
        // as selecting text and then we won't have all text selected, so we have to re-trigger selectAll.
        if (arrowKeyModeRef.current) selectAll(inputRef)
      }}
      size="small"
      placeholder={placeholder}
      aria-describedby={descriptionId}
      aria-errormessage={errorId}
      ref={mergedRef}
      className={clsx(styles.singleDateInput, activeRangeEnd && styles.activeRangeEnd, className)}
      disabled={disabled}
      aria-label={ariaLabel}
    />
  )

  return (
    <div
      className={clsx(
        styles.singleDateInputContainer,
        fullWidth && styles.fullWidth,
        calendarButton && styles.hasCalendarButton,
      )}
    >
      {isAnchor ? (
        input
      ) : (
        <FormControl>
          <FormControl.Label visuallyHidden={!labelVisible}>{ariaLabel}</FormControl.Label>
          {input}
        </FormControl>
      )}
      <ValidationErrorPopover id={errorId} message={validity.message} position="above" />
      <HiddenDescription id={descriptionId}>
        {!disableShortcuts ? DateResources.shortcutInputDescription : DateResources.dateInputDescription}
      </HiddenDescription>
    </div>
  )
}

SingleDateInput.displayName = 'SingleDateInput'

const CalendarButton = ({onAction}: {onAction: (event: React.KeyboardEvent | React.MouseEvent) => void}) => (
  <TextInput.Action
    icon={CalendarIcon}
    aria-label="Open date picker"
    onClick={onAction}
    onKeyDown={onAction}
    className={styles.calendarIconButton}
  />
)

type DatePickerTextInputProps = {
  ref?: React.Ref<DatePickerTextInput>
  fullWidth?: boolean
  /** If this is used as the anchor, this ref is given to the `AnchoredOverlay` to position the overlay. */
  anchorRef?: React.Ref<AnchorElement>
  disabled?: boolean
  className?: string
}

export interface DatePickerTextInput {
  focus(): void
}

/** Internal text box(es) to show inside the datepicker panel. */
export const DatePickerTextInput = ({ref, fullWidth, anchorRef, className, disabled}: DatePickerTextInputProps) => {
  const {
    selection,
    onDateInput,
    activeRangeEnd,
    setActiveRangeEnd,
    configuration: {variant, iconPlacement},
  } = useDatePickerContext()

  const isAnchor = anchorRef !== undefined

  const firstRef = useRef<HTMLInputElement>(null)
  const secondRef = useRef<HTMLInputElement>(null)

  useImperativeHandle(ref, () => ({
    focus: () => (activeRangeEnd === 'to' ? secondRef.current?.focus() : firstRef.current?.focus()),
  }))

  const onAction = useOnAnchorAction()

  const calendarButton = useMemo(() => {
    if (!isAnchor || iconPlacement === 'none') return undefined
    return <CalendarButton onAction={onAction} />
  }, [iconPlacement, isAnchor, onAction])

  const rangeEndCalendarButton = useMemo(() => {
    if (!isAnchor || iconPlacement === 'none') return undefined
    return (
      <CalendarButton
        onAction={event => {
          setActiveRangeEnd('to')
          onAction(event)
        }}
      />
    )
  }, [iconPlacement, isAnchor, onAction, setActiveRangeEnd])

  const input =
    isRangeSelection(selection) && variant === 'range' ? (
      <div ref={anchorRef} className={styles.rangeInputs}>
        <SingleDateInput
          aria-label="Start date"
          ref={firstRef}
          value={selection?.from ?? null}
          onChange={from => onDateInput({from, to: selection?.to ?? null})}
          onFocus={() => setActiveRangeEnd('from')}
          fullWidth={fullWidth}
          isAnchor={isAnchor}
          disableShortcuts={isAnchor}
          activeRangeEnd={!isAnchor && activeRangeEnd === 'from'}
          calendarButton={calendarButton}
          className={className}
          disabled={disabled}
          labelVisible={!isAnchor}
        />
        <span className={styles.rangeInputsSeparator}>{' - '}</span>
        <SingleDateInput
          aria-label="End date"
          ref={secondRef}
          value={selection?.to ?? null}
          onChange={to => onDateInput({from: selection?.from ?? to, to})}
          onFocus={() => setActiveRangeEnd('to')}
          fullWidth={fullWidth}
          isAnchor={isAnchor}
          disableShortcuts={isAnchor}
          activeRangeEnd={!isAnchor && activeRangeEnd === 'to'}
          calendarButton={rangeEndCalendarButton}
          className={className}
          disabled={disabled}
          labelVisible={!isAnchor}
        />
      </div>
    ) : isSingleSelection(selection) && variant === 'single' ? (
      <SingleDateInput
        aria-label={isAnchor ? undefined : 'Selected date'}
        ref={anchorRef ?? firstRef}
        value={selection}
        onChange={onDateInput}
        fullWidth={fullWidth}
        isAnchor={isAnchor}
        disableShortcuts={isAnchor}
        calendarButton={calendarButton}
        className={className}
        disabled={disabled}
      />
    ) : null

  return isAnchor ? input : <div className={styles.container}>{input}</div>
}

DatePickerTextInput.displayName = 'DatePickerTextInput'
